import { useAuthStore } from '@/store/auth.store';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';

interface HeaderProps {
  onMenuClick?: () => void;
  greeting?: string;
  date?: string;
  notificationsPath?: string;
}

export default function Header({ onMenuClick, greeting, date, notificationsPath = '/notifications' }: HeaderProps) {
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const apiClient = useApiClient();
  const { t } = useTranslation();

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      try {
        return await apiClient.getNotifications();
      } catch {
        return [];
      }
    },
    refetchInterval: 30000,
  });

  const unreadCount = (notifications || []).filter(
    (n: any) => n.status === 'PENDING' || n.status === 'SENT'
  ).length;

  const handleLogout = () => {
    logout();
    addToast(t('header.loggedOut'), 'success');
    navigate('/login');
  };

  return (
    <header className="bg-gradient-to-r from-white via-neutral-50 to-neutral-100 border-b border-neutral-200 px-3 sm:px-6 py-3 sm:py-5 sticky top-0 z-10 shadow-md hover:shadow-lg transition-shadow duration-300">
      {/* Mobile header */}
      <div className="lg:hidden flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="p-1.5 hover:bg-neutral-200 rounded-lg transition-all duration-300 hover:shadow-md flex-shrink-0"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5 text-neutral-700 hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <div className="min-w-0 flex-1">
            {greeting && (
              <h1 className="text-sm font-bold bg-gradient-to-r from-brand-dark to-primary-600 bg-clip-text text-transparent truncate">
                {greeting}
              </h1>
            )}
            {date && <p className="text-neutral-600 text-xs font-medium truncate">{date}</p>}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <LanguageSelector />
          <button
            onClick={() => navigate(notificationsPath)}
            className="relative p-1.5 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-300 hover:shadow-md"
            aria-label={t('header.notifications')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-accent-red to-accent-red/80 rounded-full shadow-md animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={handleLogout}
            className="px-3 py-2 text-xs sm:text-sm font-medium text-neutral-700 hover:text-white hover:bg-gradient-to-r hover:from-accent-red hover:to-accent-red/80 rounded-lg transition-all duration-300 border border-neutral-200 hover:border-accent-red/40 hover:shadow-lg whitespace-nowrap min-h-[44px] flex items-center"
          >
            {t('header.logout')}
          </button>
        </div>
      </div>

      {/* Desktop header */}
      <div className="hidden lg:flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          {greeting && (
            <div className="animate-slide-in">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-brand-dark to-primary-600 bg-clip-text text-transparent">
                {greeting}
              </h1>
              {date && <p className="text-neutral-600 text-sm mt-1 font-medium">{date}</p>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <LanguageSelector />
          <button
            onClick={() => navigate(notificationsPath)}
            className="relative p-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-300 hover:shadow-md"
            aria-label={t('header.notifications')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-gradient-to-r from-accent-red to-accent-red/80 rounded-full shadow-md animate-pulse">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm font-medium text-neutral-700 hover:text-white hover:bg-gradient-to-r hover:from-accent-red hover:to-accent-red/80 rounded-lg transition-all duration-300 border border-neutral-200 hover:border-accent-red/40 hover:shadow-lg hover:scale-105"
          >
            {t('header.logout')}
          </button>
        </div>
      </div>
    </header>
  );
}
